// src/oura/newscan/useGscState.ts
//
// Search Console for the New scan flow: the connection state of the site,
// connect (the consent tab opens inside the click), a check when the site is
// known or typing pauses, and a watch while a consent tab is open. The state
// resets when the site changes. Optional everywhere: nothing waits on it.
//
// Composed by useScanFlowState (ScanFlowContext.ts).

import { useCallback, useEffect, useRef, useState } from "react";
import { connectSearchConsole, getSearchConsoleState } from "@service/aeo/scanDraft";
import { FRAME } from "./copy";
import { scanDraftMode } from "./flags";

export type GscStatus = "connected" | "not_connected" | "action_needed" | "connecting";

export interface ScanFlowGsc {
    status: GscStatus;
    property: string | null;
    /** A connect or check failure, in a sentence. */
    error: string | null;
}

const GSC_IDLE: ScanFlowGsc = { status: "not_connected", property: null, error: null };

const GSC_WATCH_MS = 120_000;
const GSC_WATCH_EVERY_MS = 5_000;
const GSC_SETTLE_MS = 4_000;
const GSC_SETTLE_EVERY_MS = 500;
const GSC_TYPING_PAUSE_MS = 700;

/** Search Console consent: only Google's own hosts, https only. */
function safeConsentUrl(raw: string): string | null {
    try {
        const url = new URL(raw);
        if (url.protocol !== "https:") return null;
        return /^accounts\.google\.com$/i.test(url.hostname) || /(^|\.)googleapis\.com$/i.test(url.hostname) ? url.toString() : null;
    } catch {
        return null;
    }
}

export interface GscSetup {
    /** The site to connect: the draft's, else the normalised typed one. */
    gscDomain: string;
    /** The site once it is known (read, or the draft's); "" while typing. */
    settledDomain: string;
    hasDraft: boolean;
    /** Pulls the draft's searches again after a connect made here. */
    refreshSearch(): Promise<unknown>;
}

export interface GscState {
    gsc: ScanFlowGsc;
    connectGsc(): Promise<void>;
    recheckGsc(): Promise<void>;
}

export function useGscState({ gscDomain, settledDomain, hasDraft, refreshSearch }: GscSetup): GscState {
    const [gsc, setGsc] = useState<ScanFlowGsc>(GSC_IDLE);

    const pullAfterConnect = useRef(false);
    const watchTimer = useRef<number | null>(null);
    const watchUntil = useRef(0);

    const stopWatch = useCallback(() => {
        if (watchTimer.current !== null) window.clearInterval(watchTimer.current);
        watchTimer.current = null;
        watchUntil.current = 0;
    }, []);

    const latestGscDomain = useRef(gscDomain);
    latestGscDomain.current = gscDomain;

    // The typing-pause check, held so a Connect click can cancel it.
    const typingTimer = useRef<number | null>(null);
    const clearTypingTimer = useCallback(() => {
        if (typingTimer.current !== null) window.clearTimeout(typingTimer.current);
        typingTimer.current = null;
    }, []);

    // Bumped by every Connect click: an answer asked for before the latest click is stale.
    const connectSeq = useRef(0);
    /**
     * Bumped by every status check: the focus listener and the watcher can overlap, and only the
     * check asked last may set the state, so an older "not connected" never lands over a newer
     * "connected" (Jack, #4285).
     */
    const checkSeq = useRef(0);

    // A different site: the earlier state, and any connect still on its way, belong to the old one.
    useEffect(() => {
        connectSeq.current += 1;
        checkSeq.current += 1;
        pullAfterConnect.current = false;
        clearTypingTimer();
        stopWatch();
        setGsc(GSC_IDLE);
    }, [gscDomain, clearTypingTimer, stopWatch]);

    /**
     * Asks Search Console where this site stands. "connecting" only falls back to
     * "not_connected" from connect's own final check (fromConnect, keepConnecting false);
     * every other check leaves a connect in progress alone.
     */
    const check = useCallback(
        async (keepConnecting: boolean, fromConnect = false) => {
            if (!gscDomain) return;
            const seq = connectSeq.current;
            const asked = ++checkSeq.current;
            const res = await getSearchConsoleState(gscDomain);
            // The site changed while asking: this answer is for the old one.
            if (latestGscDomain.current !== gscDomain) return;
            // A Connect click came after this question was asked: its own checks answer.
            if (seq !== connectSeq.current) return;
            // A newer check was asked meanwhile: its answer is the one that counts.
            if (asked !== checkSeq.current) return;
            const mayDowngrade = fromConnect && !keepConnecting;
            if (!res.ok) {
                setGsc((g) => ({
                    ...g,
                    status: keepConnecting || (g.status === "connecting" && !mayDowngrade) ? g.status : "not_connected",
                    error: res.kind === "absent" ? null : res.message,
                }));
                return;
            }
            const { status, property } = res.data;
            setGsc((g) => {
                if (g.status === "connecting" && status === "not_connected" && !mayDowngrade) return g;
                return { status, property, error: null };
            });
            if (status === "connected") {
                if (watchTimer.current !== null) stopWatch();
                // Only after a connect made here: a page load does not pull again.
                if (pullAfterConnect.current && hasDraft) void refreshSearch();
                pullAfterConnect.current = false;
            }
        },
        [gscDomain, hasDraft, refreshSearch, stopWatch],
    );

    const recheckGsc = useCallback(() => check(false), [check]);

    // Check once whenever the site is known (after a read, or when a draft loads),
    // and once typing pauses on a site, so a site that is already connected says
    // so before the read (or when the step stops on an open-draft card).
    useEffect(() => {
        if (settledDomain) {
            void check(false);
            return;
        }
        if (!gscDomain || !gscDomain.includes(".")) return;
        typingTimer.current = window.setTimeout(() => {
            typingTimer.current = null;
            void check(false);
        }, GSC_TYPING_PAUSE_MS);
        return clearTypingTimer;
        // Only when the site changes: the check itself does not move the domain.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settledDomain, gscDomain]);

    // While a consent tab is open: re-check on focus and every 5 s for 2 minutes.
    useEffect(() => {
        const onFocus = () => {
            if (watchUntil.current > Date.now()) void check(true);
        };
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [check]);
    useEffect(() => stopWatch, [stopWatch]);

    const startWatch = useCallback(() => {
        stopWatch();
        watchUntil.current = Date.now() + GSC_WATCH_MS;
        watchTimer.current = window.setInterval(() => {
            if (Date.now() > watchUntil.current) {
                stopWatch();
                setGsc((g) => (g.status === "connecting" ? { ...g, status: "not_connected" } : g));
                return;
            }
            void check(true);
        }, GSC_WATCH_EVERY_MS);
    }, [check, stopWatch]);

    const connectGsc = useCallback(async () => {
        const domain = gscDomain;
        if (!domain) return;
        // The consent tab has to open inside the click, before any await, or the
        // browser blocks it as a popup (the DataConnectors pattern).
        const live = scanDraftMode() === "live";
        const tab = live ? window.open("", "_blank") : null;
        if (tab) tab.opener = null;
        // A pending typing-pause check, or one already in flight, must not undo this click.
        clearTypingTimer();
        checkSeq.current += 1;
        const seq = ++connectSeq.current;
        setGsc((g) => ({ ...g, status: "connecting", error: null }));
        pullAfterConnect.current = true;
        const res = await connectSearchConsole(domain);
        // The site changed, or another Connect came, while asking: this answer is for the old one.
        if (latestGscDomain.current !== domain || seq !== connectSeq.current) {
            tab?.close();
            return;
        }
        if (!res.ok) {
            pullAfterConnect.current = false;
            tab?.close();
            setGsc((g) => ({ ...g, status: "not_connected", error: res.message }));
            return;
        }
        const raw = res.data.redirectUrl;
        if (raw) {
            const url = safeConsentUrl(raw);
            if (!url) {
                pullAfterConnect.current = false;
                tab?.close();
                setGsc((g) => ({ ...g, status: "not_connected", error: FRAME.gscBadLink }));
                return;
            }
            if (tab && !tab.closed) tab.location.href = url;
            else window.open(url, "_blank", "noopener");
            startWatch();
            return;
        }
        tab?.close();
        if (live) {
            await check(false, true);
            return;
        }
        // Fixtures: the sample connection lands about a second after the call,
        // so keep asking for a few seconds instead of once.
        const until = Date.now() + GSC_SETTLE_MS;
        while (Date.now() < until && latestGscDomain.current === domain && seq === connectSeq.current) {
            await new Promise((r) => window.setTimeout(r, GSC_SETTLE_EVERY_MS));
            const state = await getSearchConsoleState(domain);
            if (state.ok && state.data.status !== "not_connected") break;
        }
        if (latestGscDomain.current === domain && seq === connectSeq.current) await check(false, true);
    }, [gscDomain, check, startWatch, clearTypingTimer]);

    return { gsc, connectGsc, recheckGsc };
}
